import React, { useState, useEffect } from 'react';
import { WithStyles } from '@material-ui/core/styles';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import { Grid, withStyles } from '@material-ui/core';

import Dashboard from '../../../../../layouts/Dashboard';
import UsersList from '../../../../../components/Global/Users/List';
import CustomSortableTable, { SortableTableHeader, SortableTableRow } from '../../../../../components/CustomSortableTable';
import CustomViewToggle, { ViewType } from '../../../../../components/CustomViewToggle';

import { selectAdminUsersInitQuery, AdminUser } from './queries';
import styles from './styles';

interface Props extends WithStyles<typeof styles>, RouteComponentProps {
  classes: ClassNameMap<string>;
}

const ListAdminUsers = ({ classes, history }: Props): React.ReactElement => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [viewType, setViewType] = useState<ViewType>(ViewType.TABLE);

  const { data: adminUserInitQuery, loading } = useQuery(selectAdminUsersInitQuery, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    let mounted = true;
    if (mounted && adminUserInitQuery) {
      const { users_admin } = adminUserInitQuery;
      setUsers(users_admin);
    }
    return () => {
      mounted = false;
    };
  }, [adminUserInitQuery]);

  const handleCreate = () => {
    history.push(`/admin/users/create`);
  };

  const handleView = (id: string) => {
    history.push(`/admin/users/${id}`);
  };

  const handleEdit = (id: string) => {
    history.push(`/admin/users/${id}/edit`);
  };

  const tableHeaders: SortableTableHeader[] = [
    { key: 'full_name', label: 'Name' },
    { key: 'email', label: 'Email' },
  ];

  const tableRows: SortableTableRow[] = users.map((user) => ({
    key: user.id,
    columns: [
      { key: 'full_name', label: user.full_name },
      { key: 'email', label: user.email },
    ],
    actions: [
      { label: 'View', onClick: () => handleView(user.id) },
      { label: 'Edit', onClick: () => handleEdit(user.id) },
    ],
  }));

  return (
    <Dashboard breadcrumbs={[{ label: 'Admin' }, { label: 'Users', link: '/admin/users' }]}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <CustomViewToggle value={viewType} handleChangeView={setViewType} />
        </Grid>
        {viewType === ViewType.TABLE && (
          <Grid item xs={12}>
            <CustomSortableTable
              title="Admin Users"
              counter={users.length}
              headers={tableHeaders}
              rows={tableRows}
              loading={loading}
              searchable={['full_name', 'email']}
              actions={[{ key: 'create', label: 'Create', onClick: () => handleCreate() }]}
            />
          </Grid>
        )}
        {viewType === ViewType.GRID && (
          <Grid item xs={12}>
            <UsersList users={users} handleCreate={handleCreate} handleEdit={handleEdit} handleView={handleView} />
          </Grid>
        )}
      </Grid>
    </Dashboard>
  );
};

export default withRouter(withStyles(styles)(ListAdminUsers));
